import Store from 'repatch';

import State from '../core/State';
import Language from '../entity/Language';
import TimeZone from '../entity/TimeZone';
import { Provider } from '../entity/User';
import { finishSpinner, startSpinner } from '../reducer/fullScreenSpinner';
import ApiErrorHandler from '../service/ApiErrorHandler';
import MessageDispatcher from '../service/MessageDispatcher';
import UserWebApi from '../service/persistent/UserWebApi';
import sleep from '../utility/sleep';
import AuthenticationUsecase from './AuthenticationUsecase';

class UserUsecase {
  private store: Store<State>;
  private apiErrorHandler: ApiErrorHandler;
  private userWebApi: UserWebApi;
  private messageDispatcher: MessageDispatcher;
  private authenticationUsecase: AuthenticationUsecase;

  constructor({
    store,
    apiErrorHandler,
    userWebApi,
    messageDispatcher,
    authenticationUsecase,
  }: {
    store: Store<State>;
    apiErrorHandler: ApiErrorHandler;
    userWebApi: UserWebApi;
    messageDispatcher: MessageDispatcher;
    authenticationUsecase: AuthenticationUsecase;
  }) {
    this.store = store;
    this.apiErrorHandler = apiErrorHandler;
    this.userWebApi = userWebApi;
    this.messageDispatcher = messageDispatcher;
    this.authenticationUsecase = authenticationUsecase;
  }

  public changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.changePassword(currentPassword, newPassword);
      await this.authenticationUsecase.getCurrentUser();
      this.messageDispatcher.notifyUnimportant('user_setting.login.change_password.password_changed');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public diconnectProvider = async (provider: Provider): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.diconnectProvider(provider);
      await this.authenticationUsecase.getCurrentUser();
      this.messageDispatcher.notifyUnimportant('user_setting.login.oauth_settings.provider_disconnected', {
        messageValues: {
          provider: Provider[provider],
        },
      });
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public changeEmail = async (email: string): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.changeEmail(email);
      await this.authenticationUsecase.getCurrentUser();
      this.messageDispatcher.notifyUnimportant('user_setting.email.email_changed');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public resetChangeEmail = async (): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.resetChangeEmail();
      await this.authenticationUsecase.getCurrentUser();
      this.messageDispatcher.notifyUnimportant('user_setting.email.change_email_reseted');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public updateGeneral = async ({
    allowPublicProfile,
    allowPublicSearch,
    receivingEmails,
    comment,
    lang,
    timeZone,
    name,
  }: {
    allowPublicProfile: boolean;
    allowPublicSearch: boolean;
    receivingEmails: boolean;
    comment?: string;
    lang: Language;
    timeZone: TimeZone;
    name: string;
  }): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.updateGeneral({
        allowPublicProfile,
        allowPublicSearch,
        receivingEmails,
        comment,
        lang,
        timeZone,
        name,
      });
      await this.authenticationUsecase.getCurrentUser();
      this.messageDispatcher.notifyUnimportant('user_setting.general.settings_updated');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public createOtpSecretKey = async (): Promise<{ otpSecretKey: string; provisioningUri: string }> => {
    try {
      this.store.dispatch(startSpinner());
      return await this.userWebApi.createOtpSecretKey();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public enableOtp = async (otpCode: string): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.enableOtp(otpCode);
      await this.authenticationUsecase.getCurrentUser();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public disableOtp = async (): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.disableOtp();
      await this.authenticationUsecase.getCurrentUser();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public uploadAvatarImage = async (files: File[] | FileList): Promise<void> => {
    const fileType = 'image/'; // Currently allow only thumbnail image
    try {
      this.store.dispatch(startSpinner());
      for (let i = 0; i < files.length; i++) {
        if (!files[i].type.startsWith(fileType)) {
          throw new Error(`message.uploading_file_must_be_image`);
        }
      }
      await this.userWebApi.uploadAvatarImage(files[0]);
      await this.authenticationUsecase.getCurrentUser();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public deleteUser = async (): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.deleteUser();
      this.messageDispatcher.notifyUnimportant('user_setting.general.user_deleted');
      await sleep(400);
      document.location.href = 'https://kaizenplatform.com/';
    } catch (_err) {
      const err: any = _err;
      if (
        err.response &&
        err.response.data &&
        err.response.data.code &&
        err.response.data.code == 'kaizen_id/user_cant_destroyable'
      ) {
        return Promise.reject();
      } else {
        this.apiErrorHandler.handle(err);
      }
      return Promise.resolve();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };

  public updateUserLang = async (lang: Language): Promise<void> => {
    try {
      this.store.dispatch(startSpinner());
      await this.userWebApi.updateUserLang(lang);
      await this.authenticationUsecase.getCurrentUser();
      this.messageDispatcher.notifyUnimportant('user_setting.general.lang_updated');
      return Promise.resolve();
    } catch (err) {
      this.apiErrorHandler.handle(err);
      return Promise.reject();
    } finally {
      this.store.dispatch(finishSpinner());
    }
  };
}

export default UserUsecase;
